import { api } from 'aws-blocks';
import { useEffect, useMemo, useState } from 'react';
import EmoteIcon from '../../Components/EmoteIcon';
import BkButton from '../../Components/ui/BkButton';
import BkPanel from '../../Components/ui/BkPanel';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import {
  canUseEmote,
  EMOTES,
  EMOTE_LOADOUT_SIZE,
  emoteUnlockHint,
  resolveEmoteLoadout,
} from '../../emotes/registry';
import useApiAction from '../../hooks/use-api-action';

export default function ProfileEmotes() {
  const { userData } = useAuthContext();
  const { addSnackBar } = useSnackBarContext();
  const ownedColorIds = userData?.ownedColorIds;
  const ownedEmoteIds = userData?.ownedEmoteIds;
  const initialLoadout = useMemo(
    () =>
      resolveEmoteLoadout(
        userData?.emoteLoadout,
        ownedColorIds,
        ownedEmoteIds
      ),
    [userData?.emoteLoadout, ownedColorIds, ownedEmoteIds]
  );
  const [draftLoadout, setDraftLoadout] = useState<string[]>(initialLoadout);

  useEffect(() => {
    setDraftLoadout(initialLoadout);
  }, [initialLoadout]);

  const [saveLoadout, savingLoadout] = useApiAction({
    action: async (payload: { emoteIds?: string[] }) =>
      api.equipEmoteLoadout(payload.emoteIds ?? []),
    reloadUserData: true,
  });

  function toggleLoadout(emoteId: string) {
    if (!canUseEmote(emoteId, ownedColorIds, ownedEmoteIds)) return;
    setDraftLoadout((prev) => {
      if (prev.includes(emoteId)) {
        return prev.filter((id) => id !== emoteId);
      }
      if (prev.length >= EMOTE_LOADOUT_SIZE) {
        addSnackBar({
          title: 'Loadout full',
          message: `Pick up to ${EMOTE_LOADOUT_SIZE} emotes.`,
          variant: 'info',
          timeout: 2000,
        });
        return prev;
      }
      return [...prev, emoteId];
    });
  }

  async function handleSaveLoadout() {
    try {
      await saveLoadout({ emoteIds: draftLoadout });
      addSnackBar({
        title: 'Loadout saved',
        message: 'In-match emote wheel updated.',
        variant: 'success',
        timeout: 2000,
      });
    } catch (err) {
      addSnackBar({
        title: 'ERROR',
        message: err instanceof Error ? err.message : String(err),
        variant: 'danger',
        timeout: 2500,
      });
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4 px-4 py-6 sm:px-6 lg:px-10">
      <BkPanel className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-lg font-bold uppercase tracking-wider text-bk-gold">
              Match loadout
            </h2>
            <p className="mt-1 text-sm text-bk-muted">
              Choose up to {EMOTE_LOADOUT_SIZE} emotes for the in-match wheel.
            </p>
          </div>
          <BkButton
            variant="primary"
            size="sm"
            disabled={savingLoadout || draftLoadout.length === 0}
            onClick={() => void handleSaveLoadout()}
          >
            Save loadout
          </BkButton>
        </div>
        <div className="flex flex-wrap gap-2">
          {draftLoadout.map((id) => (
            <EmoteIcon key={id} emoteId={id} size="md" />
          ))}
          {!draftLoadout.length ? (
            <span className="text-sm text-bk-muted">No emotes selected</span>
          ) : null}
        </div>
      </BkPanel>

      <BkPanel className="space-y-4">
        <div>
          <h2 className="text-lg font-bold uppercase tracking-wider text-bk-gold">
            Emotes
          </h2>
          <p className="mt-1 text-sm text-bk-muted">
            Toggle emotes into your match loadout. Season exclusives unlock on
            the Battle Pass.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {EMOTES.map((emote) => {
            const usable = canUseEmote(emote.id, ownedColorIds, ownedEmoteIds);
            const inLoadout = draftLoadout.includes(emote.id);
            return (
              <div
                key={emote.id}
                className={[
                  'flex flex-col items-center gap-2 border p-4',
                  inLoadout
                    ? 'border-bk-gold bg-bk-gold/15'
                    : usable
                      ? 'border-bk-border bg-bk-panel-raised'
                      : 'border-bk-border bg-bk-panel opacity-50',
                ].join(' ')}
              >
                <EmoteIcon emote={emote} size="lg" />
                <div className="text-center text-xs font-bold uppercase tracking-wide text-bk-text">
                  {emote.label}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-bk-muted">
                  {usable ? emote.rarity ?? 'common' : emoteUnlockHint(emote)}
                </div>
                <BkButton
                  variant={inLoadout ? 'primary' : 'ghost'}
                  size="sm"
                  className="mt-1 w-full"
                  disabled={savingLoadout || !usable}
                  onClick={() => toggleLoadout(emote.id)}
                >
                  {inLoadout ? 'In loadout' : 'Add to loadout'}
                </BkButton>
              </div>
            );
          })}
        </div>
      </BkPanel>
    </div>
  );
}
